
import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { SessionService } from '@evertel/session/SessionService'
import { PollConfig } from '@evertel/session/PollConfig'
import { Api } from '@evertel/api'

import debugModule from 'debug'
const debug = debugModule('app:MessageWallController')

const UNREAD_COUNTS_POLL_INTERVAL_MS = 1000 * 15 // 15 seconds

export interface RoomUnreads {
    roomId: number
    departmentId: number
    count: number
    urgent: number
    messageIds: number[]
    repliesCount: number
    unreadRepliesMap: {
        [key: string | number]: {
            repliesToId: number
            unreadCount: number
            urgentCount: number
        }
    }
}

export interface ThreadUnreads {
    count: number
    messageIds: number[]
    threadId: number
    urgent: number
}

class UnreadCountsState {
    roomUnreads: RoomUnreads[] = []
    threadUnreads: ThreadUnreads[] = []
    private pollInterval = undefined // holds the setInterval timer
    fetchUnreadCounter = 0
    hasUserSentMessages = false

    constructor(
        private api: Api,
        private sessionService: SessionService
    ) {
        makeAutoObservable(this)
    }

    startPoll = () => {
        this.stopPoll()
        if (!PollConfig.globalEnabled) return

        this.pollInterval = setInterval(() => {
            this.fetchUnreadCounts()
        }, UNREAD_COUNTS_POLL_INTERVAL_MS)
    }

    stopPoll = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval)
            this.pollInterval = undefined
        }
    }

    // Extend the interval timer if its set
    resetPollInterval = () => {
        if (this.pollInterval) {
            this.startPoll()
        }
    }

    fetchUnreadCounts = async () => {
        this.resetPollInterval()
        if (!this.sessionService.currentUserId) return

        await Promise.all([
            this.fetchRoomMessagesCountUnread(),
            this.fetchThreadMessagesCountUnread()
        ]).catch((error: any) => {
            console.error(error.message)
        })
        runInAction(() => {
            this.fetchUnreadCounter++
        })
    }

    fetchRoomMessagesCountUnread = async () => {
        const result = await this.api.Routes.BlueUser.getRoomMessagesCountUnread(this.sessionService.currentUserId)
        runInAction(() => {
            this.roomUnreads = result
        })
    }

    fetchThreadMessagesCountUnread = async () => {
        const results = await this.api.Routes.BlueUser.getThreadMessagesCountUnread(this.sessionService.currentUserId)
        runInAction(() => {
            this.threadUnreads = results
        })
    }

    checkUserMessages = async () => {
        // already true, don't call again
        if (this.hasUserSentMessages) return

        if (!this.sessionService.currentUserId) {
            runInAction(() => {
                this.hasUserSentMessages = false
            })
            return
        }

        try {
        // Run both API requests in parallel
            const [roomMessagesCount, threadMessagesCount] = await Promise.all([
                this.api.Routes.BlueUser.getRoomMessagesCount(
                    this.sessionService.currentUserId,
                    { where: { ownerId: this.sessionService.currentUserId } }
                ),
                this.api.Routes.BlueUser.getThreadMessagesCount(
                    this.sessionService.currentUserId,
                    { where: { ownerId: this.sessionService.currentUserId } }
                )
            ])

            const hasRoomMessages = roomMessagesCount?.count > 0
            const hasThreadMessages = threadMessagesCount?.count > 0

            runInAction(() => {
                this.hasUserSentMessages = hasRoomMessages || hasThreadMessages
            })

        } catch (error: any) {
            console.error('Error checking user messages:', error)
            runInAction(() => {
                this.hasUserSentMessages = false
            })
        }
    }

    getRoomUnread = (roomId: number) => {
        return this.roomUnreads?.find(u => u.roomId === roomId)
    }

    getThreadUnread = (threadId: number) => {
        return this.threadUnreads?.find(u => u.threadId === threadId)
    }

    getDeptRoomUnreads = (deptId: number) => {
        return this.roomUnreads?.filter(d => d.departmentId === deptId)
    }

    getReplyUnread = (roomId: number, repliesToId: number) => {
        const roomUnreads = this.getRoomUnread(roomId)
        const replyUnreads = roomUnreads?.unreadRepliesMap[repliesToId]

        return {
            unreadCount: replyUnreads?.unreadCount,
            urgentCount: replyUnreads?.urgentCount
        }
    }

    getRepliesUrgentCount = (roomId: number) => {
        const room = this.getRoomUnread(roomId)
        if (room?.unreadRepliesMap) {
            return Object.values(room?.unreadRepliesMap).some((value: any) => value.urgentCount)
        }
        return false
    }

    markAllRepliesAsRead = async (roomId: number) => {
        try {
            const where = { repliesToId: { neq: null } }

            await this.api.Routes.Room.postMessagesReads(roomId, where)

            runInAction(() => {
                this.roomUnreads = this.roomUnreads.map(room => {
                    if (room.roomId !== roomId) {
                        return room
                    }

                    room.count -= room.repliesCount
                    room.repliesCount = 0
                    Object.entries(room.unreadRepliesMap).forEach(([key, m]) => {
                        room.urgent -= m.urgentCount
                    })

                    return {
                        ...room,
                        unreadRepliesMap: {}
                    }
                })

            })

        } catch (error) {
            debug('markAllRepliesAsRead', error.message)
        }
    }

    markReadByRoomId = async (roomId: number, repliesToId: number | null = null) => {

        const where = {
            //null, undefined, or 0 will fail this and set eq:null
            repliesToId: /^[1-9]\d*$/.test(String(repliesToId)) ? Number(repliesToId) : { eq: null }
        }

        await this.api.Routes.Room.postMessagesReads(roomId, where)

        runInAction(() => {
            const newRoomUnreads = this.roomUnreads
                .map(room => {
                    if (room.roomId !== roomId) {
                        return room
                    }

                    if (repliesToId === null) {
                        // room.count is roomCount + repliesCount, once you enter the room
                        // the count from the room should be 0, so replace it with the repliesCount only.
                        return {
                            ...room,
                            count: room.repliesCount
                        }
                    }

                    const unreadRepliesMap = room.unreadRepliesMap
                    if (unreadRepliesMap && unreadRepliesMap[repliesToId]) {
                        // When viewing a replies chain remove the replies chain counts from the
                        // main room counts
                        const repliesCountObj = unreadRepliesMap[repliesToId]
                        room.count -= repliesCountObj.unreadCount || 0
                        room.repliesCount -= repliesCountObj.unreadCount || 0
                        room.urgent -= repliesCountObj.urgentCount || 0
                        repliesCountObj.unreadCount = undefined
                        repliesCountObj.urgentCount = undefined

                        delete unreadRepliesMap[repliesToId]

                        return room
                    }

                    return room
                })
                .filter(room => room.count > 0 || Object.keys(room.unreadRepliesMap || {}).length > 0)

            this.roomUnreads = newRoomUnreads
        })
    }

    markReadByThreadId = async (threadId: number) => {
        await this.api.Routes.Thread.postMessagesReads(threadId)

        runInAction(() => {
            this.threadUnreads = this.threadUnreads?.filter(r => r.threadId !== threadId)
        })
    }

    get roomUnreadCountsByDept(): Record<number, number> {
        // this will return an object {departmentId: unreadCount} ordered by most unreads to least
        if (!this.roomUnreads?.length) return {}
        return this.roomUnreads?.reduce((a, { departmentId, count }) => {
            a[departmentId] = (a[departmentId] || 0) + count
            return a
        }, {} as Record<number, number>)
    }

    get hasRoomUnreadUrgentsByDept(): Record<number, boolean> {
        if (!this.roomUnreads?.length) return {}
        return this.roomUnreads?.reduce((a, { departmentId, urgent }) => {
            if (urgent > 0) {
                a[departmentId] = true
            }
            return a
        }, {} as Record<number, boolean>)
    }

    get threadsMessageUnreadCount(): number {
        return this.threadUnreads.reduce((total, thread) => total + thread.count, 0)
    }

    get hasThreadUrgents(): boolean {
        return this.threadUnreads.some(t => (t.urgent || 0) > 0)
    }

    get totalUnreadCount(): number {
        const allUnreads = [...this.roomUnreads, ...this.threadUnreads]
        return allUnreads.reduce((total, unreadRecord) => total + unreadRecord.count, 0)
    }
}

decorate(injectable(), UnreadCountsState)
decorate(inject(Api), UnreadCountsState, 0)
decorate(inject(SessionService), UnreadCountsState, 1)

export { UnreadCountsState }
